import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Input, Space, Tooltip, Upload, message, InputNumber, Select } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ImportOutlined, DownloadOutlined } from "@ant-design/icons";
import { 
    useGetMajorsPaginatedQuery, 
    useDeleteMajorMutation, 
    useAddMajorMutation, 
    useImportMajorsMutation 
} from "@/redux/rtk/features/major/majorApi";
import UpdateMajor from "./UpdateMajor";
import TableComponent from "@/components/CommonUi/TableComponent";
import { AdvancedFilter } from "@/components/CommonUi/AdvancedFilter";
import useMajorFilter from "./useMajorFilter";
import * as XLSX from "xlsx";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import usePermissions from "@/utils/usePermissions";

export default function GetAllMajor() {
    const { t } = useTranslation();
    const { hasPermission } = usePermissions();
    
    const [pageConfig, setPageConfig] = useState({
        page: 1,
        count: 10,
    });
    
    const { data, isLoading } = useGetMajorsPaginatedQuery(pageConfig);
    const [deleteMajorMutation] = useDeleteMajorMutation();
    const [addMajorMutation] = useAddMajorMutation();
    const [importMajorsMutation] = useImportMajorsMutation();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedMajor, setSelectedMajor] = useState(null);
    const [newMajorName, setNewMajorName] = useState("");
    const [newMajorPrice, setNewMajorPrice] = useState("");
    const [newMajorCurrency, setNewMajorCurrency] = useState("USD");
    const [scholarships, setScholarships] = useState([{ percentage: null, price: null }]);
    
    const [filtersOpen, setFiltersOpen] = useState(false);
    const advancedFilters = useMajorFilter();

    const currencies = [
        { label: "USD", value: "USD" },
        { label: "EUR", value: "EUR" },
        { label: "TRY", value: "TRY" },
        { label: "GBP", value: "GBP" },
    ];

    const handleDelete = (id) => {
        Modal.confirm({
            title: t("common.confirmDelete") || "Are you sure you want to delete this?",
            okText: t("common.yes"),
            okType: 'danger',
            cancelText: t("common.no"),
            onOk: async () => {
                try {
                    await deleteMajorMutation({ id }).unwrap();
                } catch (error) {
                    message.error(t("common.deleteFailed") || "Delete failed");
                }
            },
        });
    };

    const handleCreate = async () => {
        if (!newMajorName.trim()) return;

        // Check for duplicate name locally in the current data (optional since backend checks too)
        const isDuplicate = data?.getAllMajor?.some(major => major.name.toLowerCase() === newMajorName.trim().toLowerCase());
        if (isDuplicate) {
            message.warning(t("university.majorAlreadyExists") || "Major name already exists");
            return;
        }

        try {
            await addMajorMutation({ 
                name: newMajorName, 
                price: newMajorPrice, 
                currency: newMajorCurrency,
                scholarships: scholarships.filter(s => s.percentage !== null && s.price !== null)
            }).unwrap();
            
            setNewMajorName("");
            setNewMajorPrice("");
            setNewMajorCurrency("USD");
            setScholarships([{ percentage: null, price: null }]);
            setIsModalOpen(false);
        } catch (error) {
            // Error is handled by toastHandler in majorApi.js
        }
    };

    const handleImport = (file) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const json = XLSX.utils.sheet_to_json(worksheet);

                const majorsToImport = json.map(row => ({
                    name: row.Name || row.name || row["Major Name"] || row["Major"] || row["اسم التخصص"] || row["التخصص"],
                    price: row.Price || row.price || row["Major Price"] || row["MajorPrice"] || row["Price Major"] || row["السعر"] || row["سعر التخصص"],
                    currency: row.Currency || row.currency || row["العملة"] || row["نوع العملة"] || "USD",
                    priceAfterScholarship: row.PriceAfterScholarship || row.priceAfterScholarship || row["Price After Scholarship"] || row["السعر بعد المنحة"]
                })).filter(item => item.name);

                if (majorsToImport.length === 0) {
                    message.error(t("university.noValidExcelData") || "No valid data found in Excel file.");
                    return;
                }

                await importMajorsMutation({ majors: majorsToImport }).unwrap();
            } catch (error) {
                message.error(t("university.excelParseFailed") || "Failed to parse Excel file.");
            }
        };
        reader.readAsArrayBuffer(file);
        return false;
    };

    const exportTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{ Name: "Computer Science", Price: 1000, Currency: "USD" }, { Name: "Medicine", Price: 5000, Currency: "EUR" }]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "Major_Import_Template.xlsx");
    };

    const columns = [
        {
            title: t("common.id") || "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
        },
        {
            title: t("university.majorName") || "Major Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("common.price") || "Price",
            dataIndex: "price",
            key: "price",
            render: (price, record) => (
                <span>{price} {record.currency}</span>
            ),
        },
        {
            title: t("university.priceAfterScholarship") || "Price After Scholarship",
            dataIndex: "priceAfterScholarship",
            key: "priceAfterScholarship",
            render: (priceAfterScholarship, record) => (
                <span>{priceAfterScholarship} {record.currency}</span>
            ),
        },
        {
            title: t("common.action") || "Action",
            key: "action",
            width: 150,
            render: (record) => {
                const items = [];
                if (hasPermission("update-major")) {
                    items.push({
                        label: (
                            <span className="flex items-center gap-2" onClick={() => {
                                setSelectedMajor(record);
                                setIsUpdateModalOpen(true);
                            }}>
                                <EditOutlined className="text-blue-500" /> {t("common.edit") || "Edit"}
                            </span>
                        ),
                        key: "edit",
                    });
                }
                if (hasPermission("delete-major")) {
                    items.push({
                        label: (
                            <span className="flex items-center gap-2 text-red-500" onClick={() => handleDelete(record.id)}>
                                <DeleteOutlined /> {t("common.delete") || "Delete"}
                            </span>
                        ),
                        key: "delete",
                    });
                }
                return items;
            },
        },
    ];

    const addScholarshipRow = () => {
        setScholarships([...scholarships, { percentage: null, price: null }]);
    };

    const removeScholarshipRow = (index) => {
        const newScholarships = scholarships.filter((_, i) => i !== index);
        setScholarships(newScholarships);
    };

    const handleScholarshipChange = (index, field, value) => {
        const newScholarships = [...scholarships];
        newScholarships[index][field] = value;
        setScholarships(newScholarships);
    };

    return (
        <UserPrivateComponent permission={"readAll-major"}>
            <TableComponent
                columns={columns}
                list={data?.getAllMajor || []}
                total={data?.totalMajor || 0}
                loading={isLoading}
                setPageConfig={setPageConfig}
                title={t("university.majorsManagement") || "Majors Management"}
                isSearch
                filters={advancedFilters}
                componentTitle={t("navigation.major") || "Major"}
                extraFilter={
                    <AdvancedFilter
                        page="major"
                        filters={advancedFilters}
                        pageConfig={pageConfig}
                        setPageConfig={setPageConfig}
                        onOpen={() => setFiltersOpen(true)}
                    />
                }
                extra={
                    <Space>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportTemplate}
                        >
                            {t("common.template") || "Template"}
                        </Button>
                        <UserPrivateComponent permission="create-major">
                            <Upload
                                accept=".xlsx, .xls"
                                showUploadList={false}
                                beforeUpload={handleImport}
                            >
                                <Button icon={<ImportOutlined />} type="default">
                                    {t("university.importExcel") || "Import Excel"}
                                </Button>
                            </Upload>
                        </UserPrivateComponent>
                        <UserPrivateComponent permission="create-major">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsModalOpen(true)}
                            >
                                {t("university.addMajor") || "Add Major"}
                            </Button>
                        </UserPrivateComponent>
                    </Space>
                }
            />

            {/* Add Modal */}
            <Modal
                title={t("university.addMajor") || "Add Major"}
                open={isModalOpen}
                onOk={handleCreate}
                onCancel={() => setIsModalOpen(false)}
                okText={t("common.add") || "Add"}
            >
                <div className="mt-4">
                    <p className="mb-2">{t("university.majorName") || "Major Name"}:</p>
                    <Input
                        size="large"
                        placeholder={t("university.majorNamePlaceholder") || "Enter major name"}
                        value={newMajorName}
                        onChange={(e) => setNewMajorName(e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                        <p className="mb-2">{t("common.price") || "Price"}:</p>
                        <InputNumber
                            size="large"
                            className="w-full"
                            placeholder={t("university.majorPricePlaceholder") || "Enter price"}
                            value={newMajorPrice}
                            onChange={(value) => setNewMajorPrice(value)}
                        />
                    </div>
                    <div>
                        <p className="mb-2">{t("common.currency") || "Currency"}:</p>
                        <Select
                            size="large"
                            className="w-full"
                            value={newMajorCurrency}
                            onChange={setNewMajorCurrency}
                            options={currencies}
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <p className="font-bold">{t("university.scholarships") || "Scholarships"}</p>
                        <Button type="dashed" onClick={addScholarshipRow} icon={<PlusOutlined />}>
                            {t("university.addScholarship") || "Add Scholarship"}
                        </Button>
                    </div>
                    {scholarships.map((s, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 mb-3 items-end">
                            <div className="col-span-5">
                                <p className="mb-1 text-xs">{t("university.scholarshipPercentage") || "Scholarship Percentage (%)"}:</p>
                                <InputNumber
                                    className="w-full"
                                    min={0}
                                    max={100}
                                    formatter={value => `${value}%`}
                                    parser={value => value.replace('%', '')}
                                    value={s.percentage}
                                    onChange={(val) => handleScholarshipChange(index, "percentage", val)}
                                    placeholder="e.g. 50"
                                />
                            </div>
                            <div className="col-span-5">
                                <p className="mb-1 text-xs">{t("university.priceAfterScholarship") || "Price After Scholarship"}:</p>
                                <InputNumber
                                    className="w-full"
                                    value={s.price}
                                    onChange={(val) => handleScholarshipChange(index, "price", val)}
                                    placeholder="Price"
                                />
                            </div>
                            <div className="col-span-2">
                                <Button 
                                    danger 
                                    type="text" 
                                    icon={<DeleteOutlined />} 
                                    onClick={() => removeScholarshipRow(index)}
                                    disabled={scholarships.length === 1}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </Modal>

            {/* Update Modal */}
            {selectedMajor && (
                <UpdateMajor
                    open={isUpdateModalOpen}
                    data={selectedMajor}
                    onClose={() => {
                        setIsUpdateModalOpen(false);
                        setSelectedMajor(null);
                    }}
                />
            )}
        </UserPrivateComponent>
    );
}
